import { IDS_LOTTERIES_CLASSIC } from '~/utils/constants/home.js'
import type { LocationQuery } from 'vue-router'
import { fetchRulesLotterySelect, fetchLotterySelect } from '~/utils/playLottery.js'
import { ID_KENO_FR } from '~/utils/constants/idsLotteries'

type Item = (string | number | null)[]

interface NumbersBlock {
	first: Item
	last?: Item | null
}

type LotteryId = string | string[] | null | undefined

/**
 * Converts a route query value to a numeric ID.
 * When `id` is an array the first element is used; when absent the `fallback` is called.
 * @param id - Raw query param value.
 * @param fallback - Called when `id` is falsy; should return a default ID or `null`.
 */
export const getValidateId = (id: LotteryId, fallback: () => number | null): number | null => {
	if (Array.isArray(id)) {
		return Number(id[0])
	}
	return id ? Number(id) : fallback()
}

/**
 * Normalises the `extra_balls` query param into a string array, or `null` if absent/empty.
 * Handles both comma-separated strings and pre-split arrays.
 * @param lastBlock - Raw query param value for the last number block.
 */
export const getValidateLast = (lastBlock: any): string[] | null => {
	if (lastBlock === '' || !lastBlock) return null
	if (Array.isArray(lastBlock)) return lastBlock
	if (lastBlock === '') return null
	return lastBlock.split(',')
}

const getRulesLottery = (lottery: any, isMutipleBets: boolean = false) =>
	fetchRulesLotterySelect(lottery, isMutipleBets)

const sanitizeNumbers = (numbers: any, rule: any, isMultipleBets: boolean = false): (string | number | null)[] => {
	const validNumbers = numbers
		.map((numStr: any) => {
			if (numStr === null || numStr === undefined) return null
			const num = Number(numStr)
			return !isNaN(num) && num >= rule.min && num <= rule.max ? num : null
		})
		.filter((num: any): num is number => num !== null)

	if (isMultipleBets) {
		return validNumbers
	}

	while (validNumbers.length < rule.total) {
		validNumbers.push(null)
	}
	return validNumbers.slice(0, rule.total)
}

const getMultipleBets = (v: any): boolean => v === 'true'

const normalizeBalls = (v: any): string[] => {
	if (!v) return []
	if (Array.isArray(v)) return v
	if (typeof v === 'string') return v.split(',')
	return []
}

/**
 * Parses all lottery-number query parameters into a structured `{ first, last }` block,
 * sanitising each value against the lottery's min/max rules.
 * Returns `null` when the `lottery_id` param is missing or invalid.
 * @param query - Vue Router `LocationQuery` object.
 */
export const getParamsNumbers = ({ query = {} as LocationQuery }): NumbersBlock | null => {
	const {
		lottery_id,
		regular_balls: firstBalls,
		extra_balls,
		fifth_number = null,
		first_number = null,
		fourth_number = null,
		second_number = null,
		third_number = null,
		is_multiple_bets = null,
	} = query

	const lotteryId = getValidateId(lottery_id as LotteryId, () => NaN)
	const first = normalizeBalls(firstBalls)

	const last = getValidateLast(extra_balls)
	const isMultipleBets = getMultipleBets(is_multiple_bets)

	if (!lotteryId) return null

	const isClassicLottery =
		typeof lotteryId === 'number' && !isNaN(lotteryId) && IDS_LOTTERIES_CLASSIC.includes(lotteryId)
	const lottery = fetchLotterySelect(lotteryId)
	const rulesLottery = getRulesLottery(lottery, isMultipleBets)

	if (isClassicLottery) {
		const numbersClassic = [first_number, second_number, third_number, fourth_number, fifth_number]
		const response = sanitizeNumbers(numbersClassic, rulesLottery?.first_block, isMultipleBets)

		return {
			first: response,
			last: null,
		}
	}
	const conditionLotteryKenoFR = lotteryId === ID_KENO_FR ? true : isMultipleBets
	const parsedFirst = sanitizeNumbers(first, rulesLottery.first_block, conditionLotteryKenoFR)
	if (!last) {
		if (!rulesLottery.last_block) {
			return { first: parsedFirst, last: null }
		}
		const parsedLast = sanitizeNumbers([], rulesLottery.last_block, isMultipleBets)
		return { first: parsedFirst, last: parsedLast }
	}
	const parsedLast = sanitizeNumbers(last, rulesLottery.last_block, isMultipleBets)
	return {
		first: parsedFirst,
		last: parsedLast,
	}
}
